import React, { useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { NoRecordsCard } from './_SmartFieldRenderer';

/* ─── formatting ─────────────────────────────────────────────────────── */

// The vendor returns amounts as Indian comma-formatted STRINGS ("11,05,863"),
// which parseFloat truncates to 11. Strip separators before parsing.
const toNumber = (v) => {
    if (typeof v === 'number') return v;
    if (typeof v !== 'string') return NaN;
    const cleaned = v.replace(/[,\s₹]/g, '');
    if (cleaned === '' || !/^-?\d*\.?\d+$/.test(cleaned)) return NaN;
    return parseFloat(cleaned);
};

const formatINR = (amount) => {
    const n = toNumber(amount);
    if (!Number.isFinite(n)) return '—';
    return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatCompact = (amount) => {
    const n = toNumber(amount);
    if (!Number.isFinite(n)) return '—';
    const abs = Math.abs(n);
    if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
    if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
    if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
    return formatINR(n);
};

// Turn a vendor key into a readable label: "gross_total_income" -> "Gross Total
// Income". Used because the exact ITR field names are not known in advance.
const prettyLabel = (key) =>
    String(key)
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();

const isScalar = (v) => v === null || ['string', 'number', 'boolean'].includes(typeof v);

// Heuristic: does this key/value pair represent money? ITR payloads are full of
// amounts, but also of PANs, years and acknowledgement numbers — which must NOT
// be rendered as rupees.
const MONEY_HINTS = /(income|salary|tax|amount|total|gross|deduction|refund|payable|paid|value|turnover|profit|loss|interest|tds|cess|surcharge)/i;
const NON_MONEY_HINTS = /(year|pan|aadhaar|ack|acknowledg|number|date|status|name|type|form|section|code|id$)/i;

const looksLikeMoney = (key, value) => {
    if (typeof value === 'boolean' || value === null) return false;
    if (!Number.isFinite(toNumber(value))) return false;
    if (NON_MONEY_HINTS.test(key)) return false;
    return MONEY_HINTS.test(key);
};

/* ─── shared bits ────────────────────────────────────────────────────── */

const StatTile = ({ icon, label, value, sub, severity }) => (
    <div className="col-12 md:col-6 lg:col-3">
        <div className="card mb-0 h-full">
            <div className="flex justify-content-between align-items-start">
                <div className="flex flex-column gap-2 min-w-0">
                    <span className="text-600 text-sm font-medium">{label}</span>
                    <span className={`text-xl font-semibold ${severity === 'danger' ? 'text-red-500' : severity === 'success' ? 'text-green-600' : 'text-900'}`}>
                        {value}
                    </span>
                </div>
                <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2 flex-shrink-0">
                    <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true" />
                </div>
            </div>
            {sub && <span className="text-500 text-xs block mt-2">{sub}</span>}
        </div>
    </div>
);

const InfoItem = ({ label, value, money }) => (
    <div className="col-12 md:col-6 lg:col-3">
        <div className="surface-100 border-round p-3 h-full">
            <div className="text-600 text-xs font-medium mb-1">{label}</div>
            <div className="text-900 text-base font-semibold" style={{ wordBreak: 'break-word' }}>
                {money ? formatINR(value) : (value || value === 0 ? String(value) : '—')}
            </div>
        </div>
    </div>
);

const chartOptions = () => ({
    maintainAspectRatio: false,
    plugins: {
        legend: { labels: { color: 'var(--text-color-secondary)', usePointStyle: true, boxWidth: 8 } },
        tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label || ''}: ${formatINR(ctx.parsed.y ?? ctx.parsed)}` } },
    },
    scales: {
        x: { ticks: { color: 'var(--text-color-secondary)' }, grid: { color: 'var(--surface-border)' } },
        y: {
            ticks: { color: 'var(--text-color-secondary)', callback: (v) => formatCompact(v) },
            grid: { color: 'var(--surface-border)' },
        },
    },
});

const AutoTable = ({ rows, emptyText = 'No records' }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return (
            <div className="flex flex-column align-items-center py-4">
                <i className="pi pi-inbox pi-2x text-400 mb-2" aria-hidden="true" />
                <span className="text-600 text-sm">{emptyText}</span>
            </div>
        );
    }

    // Union of keys across rows — vendor rows are not always uniform.
    const cols = Array.from(rows.reduce((set, r) => {
        Object.keys(r || {}).forEach((k) => set.add(k));
        return set;
    }, new Set()));

    const bodyFor = (key) => (row) => {
        const v = row?.[key];
        if (v === null || v === undefined || v === '') return <span className="text-500">—</span>;
        if (!isScalar(v)) return <span className="text-500 text-xs">{Array.isArray(v) ? `${v.length} item(s)` : 'nested'}</span>;
        if (looksLikeMoney(key, v)) return <span className="font-medium">{formatINR(v)}</span>;
        return <span className="text-900">{String(v)}</span>;
    };

    return (
        <DataTable
            value={rows}
            paginator={rows.length > 10}
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
            paginatorClassName="text-sm"
            pageLinkSize={3}
            size="small"
            stripedRows
            showGridlines
            scrollable
            scrollHeight="flex"
            responsiveLayout="scroll"
            className="p-datatable-gridlines"
        >
            {cols.map((c, i) => (
                <Column
                    key={c}
                    field={c}
                    header={prettyLabel(c)}
                    body={bodyFor(c)}
                    sortable
                    frozen={i === 0}
                    alignFrozen="left"
                    style={{ minWidth: i === 0 ? '160px' : '130px' }}
                />
            ))}
        </DataTable>
    );
};

// Renders an arbitrary object as label/value tiles, skipping nested structures
// (those get their own panel).
const KeyValueGrid = ({ data }) => {
    const entries = Object.entries(data || {}).filter(([, v]) => isScalar(v));
    if (entries.length === 0) return <p className="text-600 text-sm m-0">No details</p>;
    return (
        <div className="grid">
            {entries.map(([k, v]) => (
                <InfoItem key={k} label={prettyLabel(k)} value={v} money={looksLikeMoney(k, v)} />
            ))}
        </div>
    );
};

/**
 * ITR Fetch — output for the borrower-consent payload.
 *
 * Deliberately SHAPE-AGNOSTIC. The GST consent flow taught that the vendor's
 * consent payload can differ completely from its upload counterpart — assuming
 * a structure there produced a screen of ₹0. The exact ITR field names are not
 * known until a real journey completes, so rather than hard-code keys that may
 * not exist, this component:
 *
 *   • pulls out identity/summary fields when they are present, by matching
 *     common ITR naming (pan, assessment_year, gross_total_income, …)
 *   • renders every remaining scalar as a labelled tile
 *   • renders every list as a table and every nested object as its own panel
 *
 * The result degrades gracefully: an unexpected payload still displays in full
 * rather than silently showing nothing. Once a real payload is observed the
 * summary tiles can be tightened.
 */
const GlibItrFetchOutputComponent = ({ reportData, hideCredits, isSharedView }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const [expanded, setExpanded] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const json = useMemo(() => {
        const rj = reportData?.report_json;
        if (!rj) return {};
        if (typeof rj !== 'string') return rj;
        try { return JSON.parse(rj); } catch { return {}; }
    }, [reportData]);

    const meta = useMemo(() => {
        if (!reportData) return null;
        return {
            isComplete: reportData.is_complete ?? (json.stage === 'completed'),
            workOrderId: reportData.work_order_id ?? json.work_order_id ?? null,
            excelUrl: (reportData.download_urls ?? json.download_urls ?? {})?.excel ?? null,
            remaining: reportData.remaining_credits ?? undefined,
            count: reportData.filings_analysed ?? json.filings_analysed ?? null,
        };
    }, [reportData, json]);

    // Each entry is { id, work_order_file, itr: <payload> }.
    const filings = useMemo(() => {
        const rows = json.filings ?? reportData?.filings ?? [];
        return Array.isArray(rows) ? rows : [];
    }, [json, reportData]);

    // Real payload shape (verified against a live 5-year fetch): each filing is
    //   { id, work_order_file, itr: { metadata: {...80 keys}, table: {...237 keys} } }
    // metadata holds identity + taxes paid; table holds the income figures and
    // Chapter VI-A deductions. An earlier draft read top-level keys and so found
    // nothing — hence the explicit sectioning below.
    const records = useMemo(() => filings.map((f) => {
        const itr = f?.itr ?? {};
        return {
            id: f?.id ?? null,
            metadata: (itr.metadata && typeof itr.metadata === 'object') ? itr.metadata : {},
            table: (itr.table && typeof itr.table === 'object') ? itr.table : {},
        };
    }), [filings]);

    // Sort newest-first so the headline tiles describe the most recent filing.
    const sorted = useMemo(
        () => [...records].sort((a, b) =>
            String(b.metadata.assessment_year ?? '').localeCompare(String(a.metadata.assessment_year ?? ''))),
        [records],
    );

    const latest = sorted[0] ?? { metadata: {}, table: {} };

    // Vendor keys vary slightly between ITR forms, so match case/space-insensitively.
    const findKey = (obj, ...candidates) => {
        const norm = (x) => String(x).toLowerCase().replace(/[^a-z0-9]/g, '');
        const keys = Object.keys(obj || {});
        for (const want of candidates) {
            const hit = keys.find((k) => norm(k) === norm(want));
            if (hit && obj[hit] !== null && obj[hit] !== '') return obj[hit];
        }
        return null;
    };

    const pan        = findKey(latest.metadata, 'PAN', 'pan');
    const name       = findKey(latest.metadata, 'Name', 'assessee_name');
    const assessYear = findKey(latest.metadata, 'assessment_year', 'assessmentYear');
    const itrType    = findKey(latest.metadata, 'itr_type', 'itrType');
    const grossIncome = findKey(latest.table, 'Gross Total income', 'Gross Total Income', 'gross_total_income');
    const totalIncome = findKey(latest.table, 'Total income', 'Total Income', 'total_income');
    const taxPaid     = findKey(latest.metadata, 'Total-Taxes-Paid', 'Total Taxes Paid', 'total_tax_paid');

    // Income across assessment years — the clearest signal in a multi-year fetch.
    const incomeChart = useMemo(() => {
        const points = records
            .map((r) => ({
                year: findKey(r.metadata, 'assessment_year'),
                gross: toNumber(findKey(r.table, 'Gross Total income', 'Gross Total Income')),
                paid: toNumber(findKey(r.metadata, 'Total-Taxes-Paid')),
            }))
            .filter((pt) => pt.year && Number.isFinite(pt.gross));

        if (points.length < 2) return null;
        points.sort((a, b) => String(a.year).localeCompare(String(b.year)));

        return {
            labels: points.map((p) => p.year),
            datasets: [
                {
                    label: 'Gross Total Income',
                    data: points.map((p) => p.gross),
                    backgroundColor: 'var(--primary-color)',
                },
                {
                    label: 'Taxes Paid',
                    data: points.map((p) => (Number.isFinite(p.paid) ? p.paid : 0)),
                    backgroundColor: 'var(--green-400)',
                },
            ],
        };
    }, [records]);

    // One row per assessment year for the comparison table.
    const yearRows = useMemo(() => sorted.map((r) => ({
        'Assessment Year': findKey(r.metadata, 'assessment_year') ?? '—',
        'ITR Form': findKey(r.metadata, 'itr_type') ? `ITR-${findKey(r.metadata, 'itr_type')}` : '—',
        'Gross Total Income': findKey(r.table, 'Gross Total income', 'Gross Total Income') ?? '—',
        'Total Income': findKey(r.table, 'Total income', 'Total Income') ?? '—',
        'Total Taxes Paid': findKey(r.metadata, 'Total-Taxes-Paid') ?? '—',
        'TDS': findKey(r.metadata, 'Total-TDS') ?? '—',
        'Acknowledgement': findKey(r.metadata, 'acknowledgement_number') ?? '—',
    })), [sorted]);

    if (!reportData) return <NoRecordsCard subtitle="No ITR data available" />;

    // A DEFERRED stub is not an empty report (GH #455). getFullDetails ships
    // `{ report_id, deferred: true }` and leaves the 200KB filing set to be
    // fetched on demand, so for a moment this component holds a record that has
    // no body yet. Rendering that as "the provider returned no filing data —
    // run the fetch again" is wrong twice over: the data exists, and the advice
    // costs the tenant another 25 credits for a report already paid for.
    const awaitingBody = !!reportData.deferred && !reportData.report_json;

    const hasData = records.length > 0 && (Object.keys(latest.metadata).length > 0 || Object.keys(latest.table).length > 0);

    const downloadExcel = () => {
        if (!meta?.excelUrl) return;
        setDownloading(true);
        window.open(meta.excelUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => setDownloading(false), 800);
    };

    // Body still on its way. Say so, and offer nothing that spends credits —
    // every tile would read "—" and every count 0, which is indistinguishable
    // from a genuinely empty report.
    if (awaitingBody) {
        return (
            <Card className="mb-3">
                <div className="flex align-items-center gap-3">
                    <i className="pi pi-spin pi-spinner text-primary text-xl" aria-hidden="true" />
                    <div>
                        <div className="font-medium text-900">Loading the filed returns…</div>
                        <div className="text-700 text-sm">
                            This report is already fetched — retrieving the filings now.
                        </div>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <>
            <Card className="mb-3">
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                    <h2 className="text-lg font-semibold text-900 m-0">
                        <i className="pi pi-file-check mr-2 text-primary" aria-hidden="true" />
                        Income Tax Returns
                    </h2>
                    <div className="flex flex-wrap align-items-center gap-2">
                        {meta?.isComplete && (
                            <Tag severity="success" icon="pi pi-check-circle" value="Completed" className="text-sm" />
                        )}
                        {!shouldHideCredits && meta?.remaining !== undefined && meta.remaining !== null && (
                            <Tag severity="success" icon="pi pi-wallet" value={`Remaining: ${meta.remaining}`} className="text-sm" />
                        )}
                    </div>
                </div>

                <div className="grid">
                    <StatTile
                        icon="pi-file"
                        label="Filings Retrieved"
                        value={meta?.count ?? filings.length ?? '—'}
                        sub={assessYear
                            ? `Latest AY ${assessYear}${itrType ? ` · ITR-${itrType}` : ''}`
                            : 'From the income-tax portal'}
                    />
                    <StatTile
                        icon="pi-id-card"
                        label="PAN"
                        value={pan || '—'}
                        sub={name ? String(name) : 'Taxpayer identity'}
                    />
                    <StatTile
                        icon="pi-arrow-up"
                        label="Gross Total Income"
                        value={grossIncome !== null ? formatINR(grossIncome) : '—'}
                        sub={totalIncome !== null ? `Taxable ${formatINR(totalIncome)}` : 'As declared in the return'}
                    />
                    <StatTile
                        icon="pi-wallet"
                        label="Total Tax Paid"
                        value={taxPaid !== null ? formatINR(taxPaid) : '—'}
                        sub="Including TDS and advance tax"
                    />
                </div>

                {hasData && (
                    <div className="flex justify-content-center mt-3">
                        <Button
                            label={expanded ? 'Hide detailed analysis' : 'View detailed analysis'}
                            icon={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                            iconPos="right"
                            text
                            onClick={() => setExpanded((v) => !v)}
                            aria-expanded={expanded}
                            className="w-full md:w-auto"
                        />
                    </div>
                )}
            </Card>

            {!hasData ? (
                <Card>
                    <Message
                        severity="warn"
                        className="w-full"
                        content={
                            <div className="text-sm">
                                <div className="font-medium mb-1">No ITR data returned</div>
                                <div className="text-700">
                                    The consent completed but the provider returned no filing data.
                                    Download the Excel workbook, or run the fetch again.
                                </div>
                            </div>
                        }
                    />
                </Card>
            ) : expanded ? (
                <Card>
                    <TabView renderActiveOnly scrollable>
                        <TabPanel header="Year on Year" leftIcon="pi pi-chart-bar mr-2">
                            <Panel header="Filings by Assessment Year" className="mb-3">
                                <AutoTable rows={yearRows} emptyText="No filings" />
                            </Panel>
                            {incomeChart && (
                                <Panel header="Income & Tax Trend" className="mb-3">
                                    <Chart type="bar" data={incomeChart} options={chartOptions()} style={{ height: '320px' }} />
                                </Panel>
                            )}
                        </TabPanel>

                        <TabPanel header="Taxpayer" leftIcon="pi pi-user mr-2">
                            <Panel header="Identity & Contact" className="mb-3">
                                <KeyValueGrid data={latest.metadata} />
                            </Panel>
                        </TabPanel>

                        <TabPanel header="Income & Deductions" leftIcon="pi pi-wallet mr-2">
                            <Panel header={`Return Details${assessYear ? ` — AY ${assessYear}` : ''}`} className="mb-3">
                                <KeyValueGrid data={latest.table} />
                            </Panel>
                        </TabPanel>

                        {sorted.length > 1 && (
                            <TabPanel header="All Filings" leftIcon="pi pi-copy mr-2">
                                {sorted.map((r, i) => {
                                    const ay = findKey(r.metadata, 'assessment_year') ?? `Filing ${i + 1}`;
                                    return (
                                        <Panel
                                            key={r.id ?? i}
                                            header={`AY ${ay}`}
                                            className="mb-3"
                                            toggleable
                                            collapsed={i > 0}
                                        >
                                            <div className="text-900 text-sm font-semibold mb-2">Taxpayer</div>
                                            <KeyValueGrid data={r.metadata} />
                                            <div className="text-900 text-sm font-semibold mt-3 mb-2">Income & Deductions</div>
                                            <KeyValueGrid data={r.table} />
                                        </Panel>
                                    );
                                })}
                            </TabPanel>
                        )}
                    </TabView>

                    {meta?.workOrderId && (
                        <p className="text-500 text-xs mt-3 mb-0">
                            Provider reference: <span className="font-mono">{meta.workOrderId}</span>
                        </p>
                    )}
                </Card>
            ) : null}

            {meta?.excelUrl && (
                <Card className="mt-3">
                    <div className="flex flex-column md:flex-row md:align-items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-900 text-sm font-semibold mb-1">
                                <i className="pi pi-file-excel mr-2 text-green-600" aria-hidden="true" />
                                Download the full report
                            </div>
                            <div className="text-600 text-xs">
                                Complete ITR extract, including any rows trimmed from the tables above.
                            </div>
                        </div>
                        <Button
                            label="Download Excel"
                            icon="pi pi-download"
                            severity="success"
                            loading={downloading}
                            onClick={downloadExcel}
                            className="w-full md:w-auto"
                        />
                    </div>
                </Card>
            )}
        </>
    );
};

export default GlibItrFetchOutputComponent;
